import { Link } from "react-router-dom";
import React, { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { CalendarDays, MapPin, Clock } from "lucide-react";
import { WfButton, CreditChip } from "@/components/site/primitives";
import { EVENT } from "@/content/worldFarmers";

export const EventsV2 = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [reduce ? "0%" : "-8%", reduce ? "0%" : "8%"]);

  return (
    <section id="events" ref={ref} aria-labelledby="events-v2-heading" className="wf-grain relative overflow-hidden bg-[var(--wf-soil)]">
      <motion.img
        style={reduce ? undefined : { y }}
        src={EVENT.image}
        alt={EVENT.alt}
        loading="lazy"
        className="absolute inset-0 h-[116%] w-full -translate-y-[8%] object-cover"
      />
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />

      <div className="wf-container relative py-20 sm:py-28 lg:py-36">
        <div className="max-w-xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-[var(--wf-clay)]/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white">
            {EVENT.badge}
          </span>
          <h2 id="events-v2-heading" className="mt-5 font-serif text-4xl font-semibold leading-none text-white sm:text-6xl [text-shadow:0_2px_20px_rgba(0,0,0,0.5)]">
            {EVENT.title}
          </h2>

          {/* Glass detail card */}
          <div className="mt-7 flex flex-col gap-5 rounded-sm border border-white/20 bg-white/10 p-5 backdrop-blur-md sm:flex-row sm:items-center sm:gap-6">
            <div className="flex shrink-0 flex-col items-center rounded-sm bg-[var(--wf-maroon)] px-5 py-3 text-white">
              <span className="text-xs font-semibold uppercase tracking-[0.16em]">{EVENT.dateParts.month}</span>
              <span className="font-serif text-4xl font-semibold leading-none">{EVENT.dateParts.day}</span>
              <span className="text-xs tracking-wide opacity-90">{EVENT.dateParts.year}</span>
            </div>
            <div className="flex flex-col gap-2">
              <p className="flex items-center gap-2 text-sm text-white/90"><CalendarDays className="h-4 w-4 text-[var(--wf-sage)]" aria-hidden="true" />{EVENT.date}</p>
              <p className="flex items-center gap-2 text-sm text-white/90"><MapPin className="h-4 w-4 text-[var(--wf-sage)]" aria-hidden="true" />{EVENT.location}</p>
              <p className="flex items-center gap-2 text-xs text-white/70"><Clock className="h-3.5 w-3.5" aria-hidden="true" />{EVENT.ticketingNote}</p>
            </div>
          </div>

          <p className="mt-6 max-w-lg text-base leading-7 text-white/85">{EVENT.body}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <WfButton as={Link} to={EVENT.href} variant="light" withArrow data-testid="events-featured-event-cta-button">
              {EVENT.cta}
            </WfButton>
            <WfButton as={Link} to="/events" className="border border-white/60 bg-white/10 text-white backdrop-blur-sm hover:bg-white/20" data-testid="events-all-events-button">
              All events
            </WfButton>
          </div>
        </div>
      </div>

      <CreditChip src={EVENT.image} className="absolute bottom-3 right-3 z-10 rounded-full bg-black/45 px-3 py-1 text-[11px] font-medium text-white/90 backdrop-blur-sm hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white" />
    </section>
  );
};
